import ProductCardLg from "$lib/components/ProductList/ProductCard/ProductCardLg/ProductCardLg.svelte";
import { findSizeGuideCategory } from "$lib/utils/findSizeGuideCategory";
import ProductCard from "$lib/components/Cards/ProductCard/ProductCard.svelte";
import type { colourVariant } from "$lib/data/colours.data";
import {
  GetProductColoursDoc,
  type GetProductColoursQuery,
} from "$lib/gql/gen/codegen";
import client from "$lib/gql/apolloClient";
import { type ApolloQueryResult } from "@apollo/client";

export type IncomingProductWrapper = {
  node: {
    content: {
      product: Product;
      type: string;
    };
  };
};
export const isIncomingProductWrapper = (
  p: any,
): p is IncomingProductWrapper => {
  return (
    p &&
    typeof p === "object" &&
    "node" in p &&
    "content" in p.node &&
    "product" in p.node.content
  );
};

export type ColourOptionsProductWrapper = {
  cursor: string;
  node: Product;
};
export const isColourOptionsProductWrapper = (
  p: any,
): p is ColourOptionsProductWrapper => {
  return (
    p &&
    typeof p === "object" &&
    "cursor" in p &&
    "node" in p &&
    "__typename" in p.node
  );
};

export type NostoRecommendation = {
  divId: any;
  resultId: any;
  resultTitle: any;
  products: {
    name: string;
    displaySubtitle: string;
    variantId: string;
    productId: string;
    imageUrl: string;
    price: number;
    compareAtPrice: number;
    url: string;
  }[];
};

interface RecItem {
  name: string;
  displaySubtitle: string;
  variantId: string;
  imageUrl: string;
  price: number;
  url: string;
  currencyCode: string;
}
type TransformedNostoRecommendation = {
  divId: any;
  resultId: any;
  resultTitle: any;
  products: RecItem[];
};

export type Product = {
  badge?: any;
  collections?: any;
  handle?: string;
  material?: any;
  media?: any;
  canonicalUrl?: string | any;
  totalInventory?: number;
  id: string;
  image: any;
  images: any;
  options: any[];
  priceRange: any;
  showPrice: boolean;
  subtitle: any;
  tags: string[];
  title: string;
  variants: any;
  comparePriceRange?: any;
  compareAtPriceRange?: any;
  comparePrice?: any;
  price?: any;
  closeSearch?: any;
  __typename?: string;

  colourOptionProducts?: Product[];
  index?: number;
};
export const isProduct = (p: any): p is Product => {
  return p && typeof p === "object" && "id" in p && "title" in p;
};

type DynamicBundleProducts = (
  recomendationData: TransformedNostoRecommendation,
) => Promise<Product[]>;
export const dynamicBundleProducts: DynamicBundleProducts =
  recomendationData => {
    const recommendationProducts = recomendationData.products;
    let products: Product[] = [];
    const promises: Promise<ApolloQueryResult<GetProductColoursQuery>>[] = [];

    const loader = async (code: string) => {
      return await client.query<GetProductColoursQuery>({
        query: GetProductColoursDoc,
        variables: {
          query: code,
          options: [],
        },
      });
    };

    const itemCodes: string[] = [];

    recommendationProducts.forEach((nProd: any) => {
      const itemCode: string = nProd.tags
        .filter((t: string) => t.includes("Item Code"))
        .toString();
      itemCodes.push(itemCode);
      promises.push(loader(itemCode));
      products.push(flattenProduct(nProd));
    });

    return Promise.all(promises).then(results => {
      const colourOptionProducts: Product[][] = [];
      results.forEach((result: any) => {
        const prods = flattenProducts(result.data);
        colourOptionProducts.push(prods);
      });
      products = products.map((product: any) => {
        const id = product.productId.replace("gid://shopify/Product/", "");
        const colourOptionProduct = colourOptionProducts.find(
          (p: Product[]) => {
            return p.some((prod: Product) => {
              return prod.id.replace("gid://shopify/Product/", "") === id;
            });
          },
        );
        const rootProd: number = colourOptionProduct.findIndex((p: Product) => {
          return p.id.replace("gid://shopify/Product/", "") === id;
        });
        const filterdColourOptionProducts = colourOptionProduct
          .filter((p: Product) => {
            return p.id !== colourOptionProduct[rootProd].id;
          })
          .map((p: Product, i: number) => ({ ...p, index: i + 1 }));
        return {
          ...colourOptionProduct[rootProd],
          index: 0,
          colourOptionProducts: filterdColourOptionProducts,
        };
      });

      return products;
    });
  };

type FlattenProduct = (
  p: IncomingProductWrapper | ColourOptionsProductWrapper,
) => Product;
export const flattenProduct: FlattenProduct = p => {
  if (isIncomingProductWrapper(p)) {
    return p.node.content.product;
  }

  if (isColourOptionsProductWrapper(p)) {
    return p.node;
  }

  //Default case incase its already a s product
  return p;
};

type FlattenProducts = (products: GetProductColoursQuery) => Product[];
export const flattenProducts: FlattenProducts = products => {
  return products.products.edges.map((p: any) => {
    return p.node as Product;
  }) as Product[];
};

//Cant use the type from size.data.ts as inStock is optional. ProductCard requires it.
type SizeOption = {
  value: string;
  inStock: boolean;
};

const getSizeOptions = (product: Product): SizeOption[] => {
  //flatten the product
  // product = product?.node?.content?.product || product?.node;
  if (!isProduct(product)) {
    product = flattenProduct(product);
  }

  const options: SizeOption[] = product.options
    .find((o: any) => o.name === "size")
    ?.values.map((o: any) => {
      const value = o;
      const inStock =
        product.variants.edges.find((v: any) => {
          return (
            v.node.selectedOptions.find(
              (opt: any) => opt.name === "size" && opt.value === o,
            ) &&
            v.node.selectedOptions.find(
              (opt: any) =>
                opt.name === "colour" &&
                opt.value ===
                  product.options.find((c_opt: any) => c_opt.name === "colour")
                    ?.values[0],
            )
          );
        }).node.quantityAvailable > 0;

      return { value, inStock } as SizeOption;
    });

  return options;
};
export const getColourOptions = (
  product: Product | Product[],
): colourVariant[] => {
  //flatten the product
  if (Array.isArray(product)) {
    const options: colourVariant[] = product.map((p: Product) => {
      return p.options
        .find((opt: any) => opt.name === "colour")
        .values.map((o: any) => {
          return {
            pid: p.id,
            identifier: o,
            inStock: p.variants.edges.some(
              (v: any) => v.node.quantityAvailable > 0,
            ),
            index: p.index,
          };
        })?.[0];
    });
    return options.sort((a: any, b: any) => a.index - b.index);
  }

  if (!isProduct(product)) {
    product = flattenProduct(product);
  }

  const options: colourVariant[] = product.options
    .find((o: any) => o.name === "colour")
    ?.values.map((o: any) => {
      const identifier = o;
      const inStock =
        product.variants.edges.filter((v: any) => {
          return (
            v.node.selectedOptions.find(
              (opt: any) => opt.name === "colour" && opt.value === o,
            ) && v.node.quantityAvailable > 0
          );
        }).length > 0;

      return {
        identifier,
        inStock,
        selectedSwatch: identifier,
        pid: product.id,
      };
    });

  return options;
};

type MapDetailsToProductCard = (
  products: Product[],
  colourIndex: number,
  selectedProduct: Product,
  regionInfo: any,
) => ProductCardLg["$$prop_def"];
export const mapDetailsToProductCard: MapDetailsToProductCard = (
  products,
  colourIndex = 0,
  selectedProduct,
  regionInfo,
) => {
  return {
    id: products[colourIndex].id.replace("gid://shopify/Product/", ""),
    title: products[colourIndex].title,
    subtitle:
      selectedProduct.subtitle || products[colourIndex].material?.value || "",
    colourOptions: getColourOptions([
      products[colourIndex],
      ...products[colourIndex].colourOptionProducts,
    ]),
    sizeOptions: getSizeOptions(products[colourIndex]),
    // price: product.products.edges[0].node.priceRange.maxVariantPrice,
    price: null,
    // comparePrice: product.products.edges[0].node.compareAtPriceRange.maxVariantPrice,
    comparePrice: null,
    priceRange: products[colourIndex].priceRange,
    comparePriceRange: products[colourIndex].compareAtPriceRange,
    images: products[colourIndex].images.edges.map((i: any) => {
      return i.node;
    }),
    url: products[colourIndex].canonicalUrl?.value,
    badge: products[colourIndex].badge?.value || "",
    reverseImages: false,
    productOptions: products[colourIndex].options,
    selectedColourOption: getColourOptions(products[colourIndex])?.[0],
    productVariants: products[colourIndex].variants.edges,
    index: 0,
    fromListPage: false,
    openTray: {
      route: "",
      ids: [],
    },

    regionInfo: regionInfo,

    sizeGuide: findSizeGuideCategory("", products[colourIndex].tags),
  };
};

type MapToProductCard = (
  product: IncomingProductWrapper | Product,
  index: number,
  products: Product[],
) => ProductCard["$$prop_def"];
export const mapToProductCard: MapToProductCard = (
  product,
  index = 0,
  products,
) => {
  if (!isProduct(product)) {
    product = flattenProduct(product);
  }

  index = products.findIndex((p: Product) => {
    return (
      p.id.replace("gid://shopify/Product/", "") ===
      product.id.replace("gid://shopify/Product/", "")
    );
  });

  const mappedProduct: ProductCard["$$prop_def"] = {
    title: product.title,
    subtitle: product.subtitle,
    priceRange: product.priceRange,
    image: product.image || {
      ...product.images.edges[0].node,
      originalSrc: product.images.edges[0].node.url,
    },
    url:
      typeof product.canonicalUrl === "object"
        ? product.canonicalUrl.value
        : product.canonicalUrl,
    showPrice: product.showPrice ?? true,
    productVariants: product.variants.edges,
    productOptions: product.options,
    sizeOptions: getSizeOptions(products?.[index]),
    selectedColourOption: getColourOptions(product)?.[0],
    colourOptions: getColourOptions([
      products[index],
      ...products[index].colourOptionProducts,
    ]),
    quickBuy: true,
    id: product.id,
    index: index,

    //These won't be on the incoming product but are required to suppress errors.
    comparePriceRange: product.comparePriceRange,
    comparePrice: product.comparePrice,
    price: product.price,
    closeSearch: product.closeSearch,
  };

  return mappedProduct;
};

type GetProduct = (
  itemCode: string,
) => Promise<ApolloQueryResult<GetProductColoursQuery>>;
export const getProduct: GetProduct = async itemCode => {
  // Get the product colours using the current item's code tag to get the product handles
  return client.query<GetProductColoursQuery>({
    query: GetProductColoursDoc,
    variables: {
      query: itemCode,
      options: [],
    },
  });
};
